import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useModule } from "@/hooks/use-module";

import type {
  CampaignCreate,
  CampaignPostCreate,
  CampaignPostSummary,
  CampaignSummary,
  CampaignUpdate,
  PipelineCreate,
  PipelineUpdate,
} from "@/types/opportunity";
import type { Page } from "@/types/pagination";
import type { PipelineStageSequenceSave } from "@/types/pipeline-sequence";

import {
  archivePipeline,
  createCampaign,
  createCampaignPost,
  createPipeline,
  deleteCampaign,
  deletePipelineStageSequence,
  fetchAllCampaignPosts,
  fetchCampaign,
  fetchCampaigns,
  fetchPipelineStageSequence,
  fetchPipelines,
  fetchPlatforms,
  restorePipeline,
  savePipelineStageSequence,
  updateCampaign,
  updatePipeline,
} from "./pipelines";

// ============================================================================
// Query Keys
// ============================================================================

export const pipelineQueryKeys = {
  all: ["pipelines"] as const,
  list: (includeArchived = false) =>
    [...pipelineQueryKeys.all, "list", { includeArchived }] as const,
  stageSequence: (pipelineId: string, stageId: string) =>
    [
      ...pipelineQueryKeys.all,
      pipelineId,
      "stage",
      stageId,
      "sequence",
    ] as const,
};

export const platformQueryKeys = {
  all: ["campaign-platforms"] as const,
  list: () => [...platformQueryKeys.all, "list"] as const,
};

export const campaignQueryKeys = {
  all: ["campaigns"] as const,
  lists: () => [...campaignQueryKeys.all, "list"] as const,
  list: (filters?: { platformId?: string; isActive?: boolean }) =>
    [...campaignQueryKeys.lists(), filters] as const,
  details: () => [...campaignQueryKeys.all, "detail"] as const,
  detail: (id: string) => [...campaignQueryKeys.details(), id] as const,
  allPosts: () => [...campaignQueryKeys.all, "all-posts"] as const,
};

// ============================================================================
// Pipeline Hooks
// ============================================================================

export function usePipelines(includeArchived = false) {
  const hasMarketing = useModule("MARKETING");
  return useQuery({
    queryKey: pipelineQueryKeys.list(includeArchived),
    queryFn: () => fetchPipelines(includeArchived),
    select: (data) => data ?? [],
    enabled: hasMarketing,
  });
}

export function usePipelineMutations() {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: pipelineQueryKeys.all });
  };

  return {
    create: useMutation({
      mutationFn: (pipeline: PipelineCreate) => createPipeline(pipeline),
      onSettled: invalidate,
    }),
    update: useMutation({
      mutationFn: ({
        pipelineId,
        pipeline,
      }: {
        pipelineId: string;
        pipeline: PipelineUpdate;
      }) => updatePipeline(pipelineId, pipeline),
      onSettled: invalidate,
    }),
    archive: useMutation({
      mutationFn: (pipelineId: string) => archivePipeline(pipelineId),
      onSettled: invalidate,
    }),
    restore: useMutation({
      mutationFn: (pipelineId: string) => restorePipeline(pipelineId),
      onSettled: invalidate,
    }),
  };
}

export function usePipelineStageSequence(
  pipelineId: string,
  stageId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: pipelineQueryKeys.stageSequence(pipelineId, stageId),
    queryFn: () => fetchPipelineStageSequence(pipelineId, stageId),
    enabled: enabled && !!pipelineId && !!stageId,
  });
}

export function useSavePipelineStageSequence(
  pipelineId: string,
  stageId: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PipelineStageSequenceSave) =>
      savePipelineStageSequence(pipelineId, stageId, payload),
    onSuccess: (sequence) => {
      queryClient.setQueryData(
        pipelineQueryKeys.stageSequence(pipelineId, stageId),
        sequence,
      );
    },
  });
}

export function useDeletePipelineStageSequence(
  pipelineId: string,
  stageId: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deletePipelineStageSequence(pipelineId, stageId),
    onSuccess: () => {
      queryClient.setQueryData(
        pipelineQueryKeys.stageSequence(pipelineId, stageId),
        null,
      );
    },
  });
}

// ============================================================================
// Platform Hooks
// ============================================================================

export function usePlatforms() {
  return useQuery({
    queryKey: platformQueryKeys.list(),
    queryFn: fetchPlatforms,
    select: (data) => data ?? [],
  });
}

// ============================================================================
// Campaign Hooks
// ============================================================================

export function useCampaigns(filters?: {
  platformId?: string;
  isActive?: boolean;
}) {
  const hasMarketing = useModule("MARKETING");
  return useQuery({
    queryKey: campaignQueryKeys.list(filters),
    queryFn: () => fetchCampaigns(filters),
    select: (data: Page<CampaignSummary> | null) =>
      data ?? { data: [], total: 0 },
    enabled: hasMarketing,
  });
}

export function useAllCampaignPosts() {
  return useQuery({
    queryKey: campaignQueryKeys.allPosts(),
    queryFn: () => fetchAllCampaignPosts(),
    select: (data: CampaignPostSummary[] | null) => data ?? [],
  });
}

export function useCampaign(campaignId: string) {
  return useQuery({
    queryKey: campaignQueryKeys.detail(campaignId),
    queryFn: () => fetchCampaign(campaignId),
    enabled: !!campaignId,
  });
}

export function useCampaignMutations() {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: campaignQueryKeys.all });
  };

  return {
    create: useMutation({
      mutationFn: (campaign: CampaignCreate) => createCampaign(campaign),
      onSettled: invalidate,
    }),
    update: useMutation({
      mutationFn: ({
        campaignId,
        campaign,
      }: {
        campaignId: string;
        campaign: CampaignUpdate;
      }) => updateCampaign(campaignId, campaign),
      onSettled: invalidate,
    }),
    delete: useMutation({
      mutationFn: (campaignId: string) => deleteCampaign(campaignId),
      onSettled: invalidate,
    }),
    createPost: useMutation({
      mutationFn: (post: CampaignPostCreate) => createCampaignPost(post),
      onSettled: invalidate,
    }),
  };
}
